/**
 * Solar Farm AI Agent Service
 * 
 * Handles communication with the backend AI agent API including:
 * - HTTP requests for chat and commands
 * - WebSocket connection for real-time communication
 * - State synchronization with the UI
 */

import { ChatMessage, AppState } from '../types';

export interface AgentResponse {
  response: string;
  actions_taken: Array<{
    action: string;
    timestamp: string;
    details?: any;
  }>;
  ui_updates: {
    highlight?: string[];
    navigate_to?: string;
    filter?: any;
  };
  requires_user_input: boolean;
  suggestions: string[];
  error: string | null;
  metadata: {
    processing_time?: number;
    confidence?: number;
    [key: string]: any;
  };
  timestamp: string;
}

export interface AgentStatus {
  status: 'ready' | 'busy' | 'error' | 'offline';
  model: string;
  active_sessions: number;
  capabilities: {
    screenshot: boolean;
    ui_control: boolean;
    vision_analysis: boolean;
    javascript_execution: boolean;
  };
  settings: {
    temperature: number;
    max_tokens: number;
    frontend_url: string;
  };
}

export interface ScreenshotOptions {
  region?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  format?: 'png' | 'jpg';
  quality?: number;
}

class AgentService {
  private baseUrl: string;
  private websocket: WebSocket | null = null;
  private clientId: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private messageHandlers: Set<(message: AgentResponse) => void> = new Set();
  private statusHandlers: Set<(status: AgentStatus) => void> = new Set();
  private connected = false;
  private initializing = false;

  constructor(baseUrl = 'http://localhost:8000') {
    this.baseUrl = baseUrl;
    this.clientId = `client-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Initialize the agent service - establish WebSocket connection for UI control
   */
  async initialize(): Promise<void> {
    // Prevent multiple concurrent initializations
    if (this.initializing) {
      return;
    }
    
    // Allow re-initialization if not connected
    if (this.connected && this.websocket?.readyState === WebSocket.OPEN) {
      return;
    }
    
    this.initializing = true;
    
    try {
      
      // Establish WebSocket connection for UI control commands
      try {
        await this.connectWebSocket();
        this.connected = true;
      } catch (wsError) {
        console.warn('WebSocket connection failed, will use HTTP fallback:', wsError);
        this.connected = false; // Mark as not connected so we use HTTP
      }
      
    } catch (error) {
      console.warn('Agent service initialization failed:', error);
      this.connected = false;
    } finally {
      this.initializing = false;
    }
  }

  /**
   * Handle JavaScript execution request from backend
   */
  private handleJavaScriptExecution(data: any): void {
    try {
      
      // Execute the JavaScript code in the global scope
      const result = eval(data.code);
      
      // Log the result
      
      // Optionally send response back to backend (if needed)
      // For now, just log success
    } catch (error) {
      console.error('JavaScript execution failed:', error);
      console.error('Failed code:', data.code);
    }
  }

  /**
   * Check if the agent service is connected to backend
   */
  isConnected(): boolean {
    // Allow WebSocket connections now that we handle JS execution
    return this.connected && this.websocket?.readyState === WebSocket.OPEN;
  }

  /**
   * Send a message to the AI agent
   */
  async sendMessage(
    message: string, 
    context?: {
      currentTab?: string;
      selectedComponents?: string[];
      uiState?: any;
    }
  ): Promise<AgentResponse> {
    const messageData = {
      message,
      client_id: this.clientId,
      context: {
        current_tab: context?.currentTab,
        selected_components: context?.selectedComponents || [],
        ui_state: context?.uiState || {},
        timestamp: new Date().toISOString()
      }
    };

    
    // Try WebSocket first, fallback to HTTP if needed
    if (this.isConnected()) {
      try {
        return await this.sendWebSocketMessage(messageData);
      } catch (error) {
        return this.sendHttpMessage(messageData);
      }
    } else {
      return this.sendHttpMessage(messageData);
    }
  }

  /**
   * Send message via HTTP API
   */
  private async sendHttpMessage(messageData: any): Promise<AgentResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/api/v1/agent/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(messageData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('HTTP agent request failed:', error);
      throw error;
    }
  }

  /**
   * Send message via WebSocket
   */
  private async sendWebSocketMessage(messageData: any): Promise<AgentResponse> {
    return new Promise((resolve, reject) => {
      if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
        reject(new Error('WebSocket not connected'));
        return;
      }

      const messageId = `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const request = { ...messageData, messageId };

      // Set up temporary handler for this specific message
      const handler = (response: AgentResponse) => {
        if ((response as any).messageId === messageId) {
          this.messageHandlers.delete(handler);
          resolve(response);
        }
      };

      // Add timeout
      const timeout = setTimeout(() => {
        this.messageHandlers.delete(handler);
        reject(new Error('WebSocket message timeout'));
      }, 30000);

      this.messageHandlers.add(handler);
      this.websocket.send(JSON.stringify(request));
      
      // Clear timeout when resolved
      Promise.resolve().then(() => clearTimeout(timeout));
    });
  }

  /**
   * Establish WebSocket connection
   */
  private async connectWebSocket(): Promise<void> {
    return new Promise((resolve, reject) => {
      const wsUrl = this.baseUrl.replace('http', 'ws') + `/ws/${this.clientId}`;
      this.websocket = new WebSocket(wsUrl);

      const timeout = setTimeout(() => {
        reject(new Error('WebSocket connection timeout'));
      }, 10000);

      this.websocket.onopen = () => {
        clearTimeout(timeout);
        this.reconnectAttempts = 0;
        resolve();
      };

      this.websocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'response') {
            // Broadcast to all message handlers
            this.messageHandlers.forEach(handler => handler(data.data));
          } else if (data.type === 'status') {
            // Broadcast status updates
            this.statusHandlers.forEach(handler => handler(data.data));
          } else if (data.type === 'execute_javascript') {
            // Handle JavaScript execution request from backend
            this.handleJavaScriptExecution(data);
          } else if (data.type === 'error') {
            console.error('Agent WebSocket error:', data.message);
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      this.websocket.onclose = () => {
        this.connected = false;
        this.attemptReconnect();
      };

      this.websocket.onerror = (error) => {
        console.error('WebSocket error:', error);
        clearTimeout(timeout);
        reject(error);
      };
    });
  }

  /**
   * Attempt to reconnect WebSocket
   */
  private async attemptReconnect(): Promise<void> {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max WebSocket reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    
    setTimeout(async () => {
      try {
        await this.connectWebSocket();
        this.connected = true;
      } catch (error) {
        console.error('WebSocket reconnection failed:', error);
        this.attemptReconnect();
      }
    }, delay);
  }

  /**
   * Get current agent status - only call this when explicitly needed
   */
  async getAgentStatus(): Promise<AgentStatus> {
    const response = await fetch(`${this.baseUrl}/api/v1/agent/agent/status`);
    
    if (!response.ok) {
      throw new Error(`Failed to get agent status: ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Take a screenshot of the current UI
   */
  async takeScreenshot(options?: ScreenshotOptions): Promise<{ screenshot_path: string; timestamp: string }> {
    const url = options?.region 
      ? `${this.baseUrl}/api/v1/vision/screenshot/region`
      : `${this.baseUrl}/api/v1/vision/screenshot`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: options ? JSON.stringify(options) : undefined
    });

    if (!response.ok) {
      throw new Error(`Screenshot failed: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Analyze screen content with AI
   */
  async analyzeScreen(prompt: string, includeElements = true): Promise<any> {
    const response = await fetch(`${this.baseUrl}/api/v1/vision/analyze`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        prompt,
        include_elements: includeElements
      })
    });

    if (!response.ok) {
      throw new Error(`Screen analysis failed: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Send UI control commands
   */
  async controlUI(action: string, params: any): Promise<any> {
    const endpoints = {
      click: '/api/v1/ui/click',
      type: '/api/v1/ui/type',
      navigate: '/api/v1/ui/navigate',
      scroll: '/api/v1/ui/scroll',
      javascript: '/api/v1/ui/javascript'
    };

    const endpoint = endpoints[action as keyof typeof endpoints];
    if (!endpoint) {
      throw new Error(`Unknown UI control action: ${action}`);
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params)
    });

    if (!response.ok) {
      throw new Error(`UI control failed: ${response.statusText}`);
    }

    return await response.json();
  }

  /**
   * Update session context with current UI state
   */
  async updateSessionContext(appState: AppState): Promise<void> {
    const contextUpdate = {
      current_tab: appState.activeTab,
      selected_components: appState.selectedComponents,
      ui_state: {
        selected_site: appState.selectedSite,
        filter_criteria: appState.filterCriteria,
        timestamp: new Date().toISOString()
      }
    };

    try {
      const response = await fetch(`${this.baseUrl}/api/v1/agent/sessions/${this.clientId}/context`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contextUpdate)
      });

      if (!response.ok) {
        console.warn('Failed to update session context:', response.statusText);
      }
    } catch (error) {
      console.warn('Failed to update session context:', error);
    }
  }

  /**
   * Subscribe to real-time agent messages
   */
  onMessage(handler: (message: AgentResponse) => void): () => void {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  /**
   * Subscribe to agent status updates
   */
  onStatusChange(handler: (status: AgentStatus) => void): () => void {
    this.statusHandlers.add(handler);
    return () => this.statusHandlers.delete(handler);
  }

  /**
   * Disconnect from agent service
   */
  disconnect(): void {
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
    this.connected = false;
    this.messageHandlers.clear();
    this.statusHandlers.clear();
  }
}

// Create singleton instance
export const agentService = new AgentService();

// Initialize on import (can be called multiple times safely)
agentService.initialize().catch(console.warn);

export default AgentService;